/* ──────────────────────────────────────────────
 *  Panier unique : passage entre les lignes enregistrées (`BookingCartLine`) et les articles du
 *  tunnel (`CartItem`), reconstruits contre le catalogue vivant.
 * ────────────────────────────────────────────── */

import {
  clampPartySize,
  isAvailablePriceOption,
  isPartySizeSelectable,
  resolvePartySizeMax,
  resolvePreselectedPriceIndex,
  type PreselectedPriceResolution,
} from '@cfixe-monorepo/shared';
import type { BookingPrestation, CartItem, PriceOption } from '../types';
import { buildSelectedSupplements, toSupplementSelectionPayload } from '../utils/supplement-selection';
import type { BookingCartLine } from './booking-cart-store';

/**
 * Sort de chaque champ d'un article du tunnel dans le panier enregistré. Typé sur toutes les clés de
 * `CartItem` : un champ ajouté à l'article ne compile pas tant qu'il n'est pas classé ici, et le test
 * vérifie qu'un champ « persisted » survit à l'aller-retour (règle des trois endroits d'avant P2).
 * - `persisted` : choix du client, gardé dans la ligne ;
 * - `catalog` : relu du catalogue vivant à la reconstruction ;
 * - `step` : choisi plus loin dans le tunnel, rechoisi à chaque visite.
 */
export const CART_ITEM_FIELD_STORAGE: Record<keyof CartItem, 'persisted' | 'catalog' | 'step'> = {
  prestation: 'catalog',
  selectedPrice: 'persisted',
  selectedPriceIndex: 'catalog',
  supplements: 'persisted',
  quantity: 'persisted',
  party_size: 'persisted',
  selected_resource_id: 'step',
};

/** Ligne enregistrée d'un article du tunnel : ce que le client a choisi, rien du catalogue. */
export function toBookingCartLine(item: CartItem): BookingCartLine {
  const priceId = item.selectedPrice.price_id;
  const supplements = toSupplementSelectionPayload(item);
  return {
    prestation_id: item.prestation.prestation_id,
    // La durée accompagne l'identifiant (repli si la formule a disparu) ; un catalogue servi sans
    // identifiant de formule (serveur plus ancien) garde le rang.
    ...(priceId
      ? { price_id: priceId, duration_minutes: item.selectedPrice.duration_minutes }
      : { price_index: item.selectedPriceIndex }),
    quantity: item.quantity,
    supplement_ids: supplements.selected_supplement_ids,
    ...(supplements.supplement_quantities ? { supplement_quantities: supplements.supplement_quantities } : {}),
    ...(item.party_size !== undefined ? { party_size: item.party_size } : {}),
  };
}

/**
 * Formule d'une ligne dans le catalogue vivant. Par identifiant d'abord : une formule encore au catalogue
 * mais plus réservable est écartée (jamais remplacée par une autre, donc un autre prix) ; une formule
 * supprimée depuis retombe sur la règle de `resolvePreselectedPriceIndex`. Les lignes des anciens paniers
 * passent par la durée (site) ou le rang (tunnel).
 */
export function resolveCartLinePriceIndex(
  prices: readonly PriceOption[],
  line: Pick<BookingCartLine, 'price_id' | 'duration_minutes' | 'price_index'>,
): PreselectedPriceResolution {
  const byPosition = (index: number): PreselectedPriceResolution =>
    isAvailablePriceOption(prices[index]) ? { status: 'ok', price_index: index } : { status: 'unavailable' };

  if (line.price_id !== undefined) {
    const index = prices.findIndex((price): boolean => price.price_id === line.price_id);
    if (index >= 0) return byPosition(index);
    return resolvePreselectedPriceIndex(prices, line.duration_minutes);
  }
  if (line.duration_minutes !== undefined) return resolvePreselectedPriceIndex(prices, line.duration_minutes);
  if (line.price_index !== undefined && line.price_index < prices.length) return byPosition(line.price_index);
  return resolvePreselectedPriceIndex(prices, undefined);
}

export interface RebuiltBookingCart {
  items: CartItem[];
  /** Prestations encore au catalogue dont la formule n'est plus réservable : le tunnel le dit au client. */
  dropped: string[];
  /**
   * Prestations qu'aucun planning ne rend réservables en ligne (`is_bookable: false`, sondé sans panier au
   * chargement) : écartées plutôt que de mener à un calendrier vide, le tunnel le dit au client.
   */
  unbookable: string[];
}

/**
 * Articles du tunnel reconstruits contre le catalogue vivant. Une prestation retirée du catalogue (ou
 * hors du périmètre de ce tunnel) disparaît sans bruit ; suppléments, taille de groupe et quantité sont
 * re-bornés sur le catalogue : le panier vient du navigateur, il a pu vieillir ou être forgé.
 */
export function rebuildBookingCartItems(
  lines: readonly BookingCartLine[],
  prestations: readonly BookingPrestation[],
): RebuiltBookingCart {
  const byId = new Map(
    prestations.map((prestation): [number, BookingPrestation] => [prestation.prestation_id, prestation]),
  );
  const items: CartItem[] = [];
  const dropped: string[] = [];
  const unbookable: string[] = [];

  for (const line of lines) {
    const prestation = byId.get(line.prestation_id);
    if (!prestation || prestation.prices.length === 0) continue;
    if (prestation.is_bookable === false) {
      unbookable.push(prestation.name);
      continue;
    }
    const resolution = resolveCartLinePriceIndex(prestation.prices, line);
    if (resolution.status !== 'ok') {
      dropped.push(prestation.name);
      continue;
    }
    const partySize = isPartySizeSelectable(prestation)
      ? clampPartySize(line.party_size, resolvePartySizeMax(prestation))
      : undefined;
    const quantity = Math.min(Math.max(1, line.quantity), Math.max(1, prestation.max_quantity ?? 1));
    const linePartySize = partySize && partySize > 1 ? partySize : undefined;
    items.push({
      prestation,
      selectedPrice: prestation.prices[resolution.price_index],
      selectedPriceIndex: resolution.price_index,
      // Quantités des suppléments re-bornées au plafond de la ligne, comme la taille de groupe.
      supplements: buildSelectedSupplements(prestation.supplements, {
        selected_supplement_ids: line.supplement_ids,
        supplement_quantities: line.supplement_quantities,
        quantity,
        party_size: linePartySize,
      }),
      quantity,
      party_size: linePartySize,
    });
  }

  return { items, dropped, unbookable };
}

/** Même prestation et même formule : un seul article du panier les porte. */
export const isSameCartLine = (a: CartItem, b: CartItem): boolean =>
  a.prestation.prestation_id === b.prestation.prestation_id && a.selectedPriceIndex === b.selectedPriceIndex;

/**
 * Sélection précédente complétée par d'autres articles, sans doublon : un article qui reprend une
 * prestation et une formule déjà présentes remplace l'ancien (c'est le dernier choix du client).
 */
export function mergeCartItems(previous: readonly CartItem[], next: readonly CartItem[]): CartItem[] {
  const merged = previous.filter((item): boolean => !next.some((other): boolean => isSameCartLine(item, other)));
  return [...merged, ...next];
}
